/**
 * The welcome pack on a photo plate — live components projected onto the
 * blank pieces of a generated flat-lay photograph.
 *
 * The photo supplies everything CSS can't fake for free: real paper thickness,
 * contact shadows, surface texture, per-trade props (copper fittings for the
 * plumber, a shingle for the roofer…). The live pieces supply everything the
 * photo can't know: the client's logo, palette, type, phone and QR codes.
 * Geometry comes from `packPlates.ts` — measured boxes in the plate's own
 * 1024×1024 space. A piece meets its blank as `contain` (default), `reflow`
 * (re-laid-out at the box's size, like a resized div — no distortion) or
 * `stretch` (fills the box exactly, distorting), optionally clipped by a
 * corner `radius`, and
 * optionally warped by a `quad` — per-corner offsets turned into a CSS
 * matrix3d homography, for blanks the camera saw in slight perspective.
 *
 * Tune mode (lab only): pieces drag, corner handles warp the quad, a mono
 * panel steps x/y/size/angle/radius and picks the fit. Touching w/h moves a
 * piece to reflow, so a resize visibly moves the artwork without stretching
 * the type.
 * Everything lives in localStorage (`foundlio-plate-tune`), merged over the
 * shipped config each render; "Show JSON" prints the merged pieces block
 * ready to be baked back into `packPlates.ts`.
 *
 * The plate handles wide containers only — the phone layout keeps the CSS
 * scene composition. If the plate image fails to load, the caller falls back
 * to the CSS scene too.
 */

import { useEffect, useRef, useState } from 'react';
import { packAsset } from './assets';
import { CARD_H, CARD_W, CardBack, CardFront, type PackBusiness } from './BusinessCard';
import {
  CarMagnet,
  DoorHanger,
  HANGER_H,
  HANGER_W,
  HOLE_CY,
  HOLE_R,
  MAGNET_H,
  MAGNET_W,
  SIGN_H,
  SIGN_W,
  YardSign,
} from './PackItems';
import {
  PLATE_IMG,
  PLATES,
  fitOf,
  plateForTrade,
  type PlateConfig,
  type PlateRect,
} from './packPlates';

/** Same film grain the CSS scene wears — keeps the two modes one family. */
const GRAIN =
  `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='180' height='180'%3E` +
  `%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='2' stitchTiles='stitch'/%3E` +
  `%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E` +
  `%3Crect width='180' height='180' filter='url(%23n)'/%3E%3C/svg%3E")`;

/** Tighter, higher-frequency noise — the sensor grain sitting under the film. */
const FINE_GRAIN =
  `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='90' height='90'%3E` +
  `%3Cfilter id='f'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='1.6' numOctaves='1' stitchTiles='stitch'/%3E` +
  `%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E` +
  `%3Crect width='90' height='90' filter='url(%23f)'/%3E%3C/svg%3E")`;

type PieceKey = 'sign' | 'magnet' | 'stack' | 'cardA' | 'cardB' | 'hanger0' | 'hanger1' | 'hanger2';

/** The pieces THIS plate actually has blanks for — partial plates (the duo)
    carry no sign and no hangers, and every loop below walks this list. */
function keysOf(cfg: PlateConfig): PieceKey[] {
  const keys: PieceKey[] = [];
  if (cfg.pieces.sign) keys.push('sign');
  keys.push('magnet', 'stack', 'cardA', 'cardB');
  (cfg.pieces.hangers ?? []).forEach((_, i) => keys.push(`hanger${i}` as PieceKey));
  return keys;
}

const TUNE_LS_KEY = 'foundlio-plate-tune';
/**
 * The last non-empty tuning, kept so a mis-click on a clear/reset can't throw
 * away an evening's alignment work. Written before anything destructive.
 */
const TUNE_BACKUP_KEY = 'foundlio-plate-tune-prev';

/**
 * The hangers' contact shadow, in plate px — short, hard and down-right, so
 * fanned hangers read as lying ON each other rather than pasted side by side.
 * They need it explicitly: their die-cut mask clips the shell's own shadow.
 */
const HANGER_SHADOW = 'drop-shadow(3px 4px 2px rgba(30,34,40,0.34))';

type Overrides = Record<string, Partial<Record<PieceKey, PlateRect>>>;

function loadOverrides(): Overrides {
  try {
    return JSON.parse(localStorage.getItem(TUNE_LS_KEY) || '{}') as Overrides;
  } catch {
    return {};
  }
}

/** Stash the current tuning before discarding it, if there is any to lose. */
function backupOverrides(prev: Overrides) {
  try {
    if (Object.keys(prev).length) localStorage.setItem(TUNE_BACKUP_KEY, JSON.stringify(prev));
  } catch {
    /* private mode — nothing to protect anyway */
  }
}

/**
 * The dev server's on-disk copy (see vite.config.ts). Saving is explicit; the
 * lab reads it back only when the browser has nothing, so a wiped or different
 * browser recovers the last saved alignment instead of starting over.
 */
const TUNE_URL = '/__plate-tune';

async function saveOverridesToDisk(ov: Overrides): Promise<boolean> {
  try {
    const res = await fetch(TUNE_URL, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(ov),
    });
    return res.ok;
  } catch {
    return false;
  }
}

async function loadOverridesFromDisk(): Promise<Overrides | null> {
  try {
    const res = await fetch(TUNE_URL);
    if (!res.ok) return null;
    const parsed = (await res.json()) as Overrides;
    return parsed && Object.keys(parsed).length ? parsed : null;
  } catch {
    return null;
  }
}

function loadBackup(): Overrides | null {
  try {
    const raw = localStorage.getItem(TUNE_BACKUP_KEY);
    const parsed = raw ? (JSON.parse(raw) as Overrides) : null;
    return parsed && Object.keys(parsed).length ? parsed : null;
  } catch {
    return null;
  }
}

/** The shipped config flattened to keyed rects, with tune overrides applied.
    Only the plate's own pieces appear — a partial plate stays partial. */
function mergedRects(cfg: PlateConfig, ov: Partial<Record<PieceKey, PlateRect>> | undefined) {
  const base: Partial<Record<PieceKey, PlateRect>> = {
    sign: cfg.pieces.sign,
    magnet: cfg.pieces.magnet,
    stack: cfg.pieces.stack,
    cardA: cfg.pieces.cardA,
    cardB: cfg.pieces.cardB,
    hanger0: cfg.pieces.hangers?.[0],
    hanger1: cfg.pieces.hangers?.[1],
    hanger2: cfg.pieces.hangers?.[2],
  };
  const out = {} as Record<PieceKey, PlateRect>;
  for (const k of keysOf(cfg)) out[k] = { ...(base[k] as PlateRect), ...(ov?.[k] ?? {}) };
  return out;
}

interface Crop {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Tightest window that shows every blank, padded a little. */
function cropOf(rects: Record<PieceKey, PlateRect>, keys: PieceKey[]): Crop {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const k of keys) {
    const r = rects[k];
    const half = Math.max(r.w, r.h) / 2;
    minX = Math.min(minX, r.cx - half);
    minY = Math.min(minY, r.cy - half);
    maxX = Math.max(maxX, r.cx + half);
    maxY = Math.max(maxY, r.cy + half);
  }
  const pad = 34;
  const x = Math.max(0, Math.round(minX - pad));
  const y = Math.max(0, Math.round(minY - pad));
  return {
    x,
    y,
    w: Math.min(PLATE_IMG, Math.round(maxX + pad)) - x,
    h: Math.min(PLATE_IMG, Math.round(maxY + pad)) - y,
  };
}

const NAT: Record<PieceKey, { w: number; h: number }> = {
  sign: { w: SIGN_W, h: SIGN_H },
  magnet: { w: MAGNET_W, h: MAGNET_H },
  stack: { w: CARD_W, h: CARD_H },
  cardA: { w: CARD_W, h: CARD_H },
  cardB: { w: CARD_W, h: CARD_H },
  hanger0: { w: HANGER_W, h: HANGER_H },
  hanger1: { w: HANGER_W, h: HANGER_H },
  hanger2: { w: HANGER_W, h: HANGER_H },
};

/* ------------------------------------------------------------------ */
/* Homography                                                          */
/* ------------------------------------------------------------------ */

/** Gaussian elimination with partial pivoting for the 8×8 warp system. */
function solve8(A: number[][], bv: number[]): number[] | null {
  const n = 8;
  const M = A.map((row, i) => [...row, bv[i]]);
  for (let col = 0; col < n; col++) {
    let piv = col;
    for (let r = col + 1; r < n; r++) if (Math.abs(M[r][col]) > Math.abs(M[piv][col])) piv = r;
    if (Math.abs(M[piv][col]) < 1e-9) return null;
    [M[col], M[piv]] = [M[piv], M[col]];
    for (let r = 0; r < n; r++) {
      if (r === col) continue;
      const f = M[r][col] / M[col][col];
      for (let c2 = col; c2 <= n; c2++) M[r][c2] -= f * M[col][c2];
    }
  }
  return M.map((row, i) => row[n] / M[i][i]);
}

/** CSS matrix3d mapping the box (0,0)-(w,0)-(w,h)-(0,h) onto `q` (tl,tr,br,bl). */
function warpMatrix(w: number, h: number, q: number[][]): string | undefined {
  const src = [
    [0, 0],
    [w, 0],
    [w, h],
    [0, h],
  ];
  const A: number[][] = [];
  const bv: number[] = [];
  for (let i = 0; i < 4; i++) {
    const [x, y] = src[i];
    const [X, Y] = q[i];
    A.push([x, y, 1, 0, 0, 0, -x * X, -y * X]);
    bv.push(X);
    A.push([0, 0, 0, x, y, 1, -x * Y, -y * Y]);
    bv.push(Y);
  }
  const s = solve8(A, bv);
  if (!s) return undefined;
  const [a, b, c, d, e, f, g, hh] = s;
  return `matrix3d(${a},${d},0,${g}, ${b},${e},0,${hh}, 0,0,1,0, ${c},${f},0,1)`;
}

const quadPoints = (r: PlateRect): number[][] => {
  const off = r.quad ?? [
    [0, 0],
    [0, 0],
    [0, 0],
    [0, 0],
  ];
  return [
    [0 + off[0][0], 0 + off[0][1]],
    [r.w + off[1][0], 0 + off[1][1]],
    [r.w + off[2][0], r.h + off[2][1]],
    [0 + off[3][0], r.h + off[3][1]],
  ];
};

/** A point in the box's own frame (0..w, 0..h) → the plate's pixel space. */
function localToPlate(r: PlateRect, lx: number, ly: number) {
  const dx = lx - r.w / 2;
  const dy = ly - r.h / 2;
  const rad = (r.a * Math.PI) / 180;
  return {
    x: r.cx + dx * Math.cos(rad) - dy * Math.sin(rad),
    y: r.cy + dx * Math.sin(rad) + dy * Math.cos(rad),
  };
}

/** Where a piece's quad corner lands in plate space — the loupe magnifies it. */
function cornerPlatePoint(r: PlateRect, idx: number) {
  const p = quadPoints(r)[idx];
  return localToPlate(r, p[0], p[1]);
}

/**
 * A hanger's effective die-cut in plate px — the measured override, or the
 * hanger's own printed hole scaled onto the blank. `cx` is an offset from the
 * blank's centre line, which is where every measured hole sat.
 */
function holeOf(r: PlateRect, sx: number, sy: number) {
  const rx = r.hole?.r ?? +(HOLE_R * sx).toFixed(1);
  return {
    cx: r.hole?.cx ?? 0,
    cy: r.hole?.cy ?? +(HOLE_CY * sy).toFixed(1),
    r: rx,
    /** Vertical radius — equal to `r` unless the blank was shot at an angle. */
    ry: r.hole?.ry ?? rx,
  };
}

/**
 * The canvas a `reflow` piece is built on: the box's aspect ratio, but at the
 * same uniform scale `contain` would have used. The piece keeps its designed
 * type size and only its layout box changes — a div that got wider, not a
 * stretched image — and the result still fills the blank exactly.
 */
function reflowCanvas(rect: PlateRect, natW: number, natH: number) {
  const s = Math.min(rect.w / natW, rect.h / natH);
  return { s, w: Math.round(rect.w / s), h: Math.round(rect.h / s) };
}

/** Canvas px → plate px for a piece, whichever fit it uses. */
function pieceScale(rect: PlateRect, natW: number, natH: number) {
  const f = fitOf(rect);
  if (f === 'stretch') return { sx: rect.w / natW, sy: rect.h / natH };
  const s = f === 'reflow' ? reflowCanvas(rect, natW, natH).s : Math.min(rect.w / natW, rect.h / natH);
  return { sx: s, sy: s };
}

/**
 * Per-sheet light. The camera pass lights the whole frame at once; this lights
 * each piece on its own, which is what actually sells printed stock — a lit
 * edge where the source falls, a little shading away from it, so every sheet
 * reads as its own surface rather than a flat patch of the photo.
 *
 * The gradient is counter-rotated by the piece's angle: the source hangs in
 * ONE place above the table, so the highlight must not turn with the paper.
 */
function Gloss({ a }: { a: number }) {
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0"
      style={{
        background:
          `linear-gradient(${(157 - a).toFixed(1)}deg,` +
          'rgba(255,255,255,0.52) 0%, rgba(255,255,255,0.16) 20%, rgba(255,255,255,0) 40%,' +
          'rgba(30,34,40,0.045) 74%, rgba(30,34,40,0.10) 100%)',
      }}
    />
  );
}

/* ------------------------------------------------------------------ */
/* A piece on its blank                                                */
/* ------------------------------------------------------------------ */

function Fit({
  rect,
  crop,
  natW,
  natH,
  selected,
  tuneMode,
  hud = 1,
  dropShadow,
  hole,
  onHoleDown,
  onPointerDown,
  onCornerDown,
  children,
}: {
  rect: PlateRect;
  crop: Crop;
  natW: number;
  natH: number;
  selected?: boolean;
  /** Tune mode is on — draw every box's w×h contour, not just the selected one. */
  tuneMode?: boolean;
  /** 1/plate-zoom — keeps handles a constant size on screen. */
  hud?: number;
  /**
   * CSS filter for the piece, in plate px. Masked pieces need it: a mask
   * clips to its own painting area, so the shell's box-shadow — which paints
   * outside the element — is cut away entirely. A drop-shadow on the wrapper
   * OUTSIDE the mask shadows the composited silhouette instead, hole and all.
   */
  dropShadow?: string;
  /** Hanger die-cut in plate px, drawn as a draggable ring in tune mode. */
  hole?: { cx: number; cy: number; r: number; ry: number };
  onHoleDown?: (mode: 'move' | 'rx' | 'ry') => (e: React.PointerEvent) => void;
  onPointerDown?: (e: React.PointerEvent) => void;
  onCornerDown?: (idx: number) => (e: React.PointerEvent) => void;
  children: React.ReactNode;
}) {
  // `cw`/`ch` is the canvas the child was built on: its natural footprint,
  // or — in reflow — a box-shaped one the caller already sized it to.
  const rb = fitOf(rect) === 'reflow' ? reflowCanvas(rect, natW, natH) : null;
  const stretch = fitOf(rect) === 'stretch';
  const cw = rb ? rb.w : natW;
  const ch = rb ? rb.h : natH;
  const sx = rb ? rb.s : stretch ? rect.w / natW : Math.min(rect.w / natW, rect.h / natH);
  const sy = rb ? rb.s : stretch ? rect.h / natH : sx;
  const q = quadPoints(rect);
  const warped = rect.quad ? warpMatrix(rect.w, rect.h, q) : undefined;
  return (
    <div
      className="absolute"
      style={{ left: rect.cx - crop.x, top: rect.cy - crop.y, width: 0, height: 0 }}
    >
      <div
        style={{
          transform: `translate(-50%, -50%) rotate(${rect.a}deg)`,
          width: rect.w,
          height: rect.h,
        }}
      >
        {/* The box that meets the blank: warped, clipped, dressed. In tune
            mode every box draws its w×h contour — contain-fit content is
            centred and won't visibly grow on the non-binding axis, so the
            contour is the only reliable feedback that a resize landed. */}
        <div
          onPointerDown={onPointerDown}
          style={{
            position: 'absolute',
            inset: 0,
            transform: warped,
            transformOrigin: '0 0',
            borderRadius: rect.radius ?? 0,
            overflow: rect.radius ? 'hidden' : undefined,
            outline: selected
              ? '2px dashed #d946ef'
              : tuneMode
                ? '1px dashed rgba(217,70,239,0.35)'
                : undefined,
            outlineOffset: 2,
            cursor: onPointerDown ? 'move' : undefined,
            touchAction: onPointerDown ? 'none' : undefined,
          }}
        >
          <div
            style={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              width: cw * sx,
              height: ch * sy,
              filter: dropShadow,
            }}
          >
            <div
              className="origin-top-left"
              style={{ transform: `scale(${sx}, ${sy})`, width: cw }}
            >
              {children}
            </div>
          </div>
        </div>
        {/* The die-cut, when this hanger is selected: drag the ring to move
            the hole onto the photo's own punched one, drag the rim dot to
            resize it. Drawn in the box's unwarped frame, which is the frame
            the mask is built in, so what you drag is what gets cut. */}
        {selected && hole && onHoleDown ? (
          <div
            className="absolute z-20"
            style={{
              left: hole.cx + rect.w / 2,
              top: hole.cy,
              width: 0,
              height: 0,
            }}
          >
            <div
              onPointerDown={onHoleDown('move')}
              className="absolute rounded-[50%]"
              style={{
                left: -hole.r,
                top: -hole.ry,
                width: hole.r * 2,
                height: hole.ry * 2,
                border: `${1.5 * hud}px dashed #22d3ee`,
                boxShadow: `0 0 0 ${1 * hud}px rgba(255,255,255,0.75)`,
                cursor: 'move',
                touchAction: 'none',
              }}
            />
            {/* Side handle widens, bottom handle heightens — a punched circle
                photographs as an ellipse when the camera is off-axis. */}
            {([
              { mode: 'rx' as const, left: hole.r, top: 0, cursor: 'ew-resize' },
              { mode: 'ry' as const, left: 0, top: hole.ry, cursor: 'ns-resize' },
            ]).map(hnd => (
              <div
                key={hnd.mode}
                onPointerDown={onHoleDown(hnd.mode)}
                className="absolute rounded-full border-2 border-white bg-cyan-400 shadow"
                style={{
                  left: hnd.left,
                  top: hnd.top,
                  width: 11 * hud,
                  height: 11 * hud,
                  transform: 'translate(-50%, -50%)',
                  cursor: hnd.cursor,
                  touchAction: 'none',
                }}
              />
            ))}
          </div>
        ) : null}

        {/* Corner handles — drag to pull the quad out of square. Open ring
            with a hairline crosshair: the corner you are aiming at stays
            visible THROUGH the handle, which a filled dot hid. Counter-scaled
            by `hud` so it keeps one size on screen whatever the plate's zoom. */}
        {selected && onCornerDown
          ? q.map((p, i) => (
              <div
                key={i}
                onPointerDown={onCornerDown(i)}
                className="absolute z-20"
                style={{
                  left: p[0],
                  top: p[1],
                  width: 0,
                  height: 0,
                  cursor: 'grab',
                  touchAction: 'none',
                }}
              >
                <svg
                  width={34}
                  height={34}
                  viewBox="0 0 34 34"
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    transform: `translate(-50%, -50%) scale(${hud})`,
                    overflow: 'visible',
                  }}
                >
                  {/* white under-stroke keeps the ring legible on any photo */}
                  <circle cx={17} cy={17} r={8.5} fill="none" stroke="#fff" strokeWidth={3.5} />
                  <circle cx={17} cy={17} r={8.5} fill="none" stroke="#d946ef" strokeWidth={1.75} />
                  {[
                    [17, 2, 17, 8],
                    [17, 26, 17, 32],
                    [2, 17, 8, 17],
                    [26, 17, 32, 17],
                  ].map(([x1, y1, x2, y2], j) => (
                    <g key={j}>
                      <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="#fff" strokeWidth={3} />
                      <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="#d946ef" strokeWidth={1.25} />
                    </g>
                  ))}
                  {/* the exact point, small enough to aim past */}
                  <circle cx={17} cy={17} r={1.35} fill="#d946ef" />
                </svg>
              </div>
            ))
          : null}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Tune panel                                                          */
/* ------------------------------------------------------------------ */

function Stepper({
  label,
  onStep,
  steps,
  value,
}: {
  label: string;
  value: string;
  steps: number[];
  onStep: (d: number) => void;
}) {
  return (
    <div className="flex items-center gap-1">
      <span className="w-4 text-zinc-500">{label}</span>
      {steps.map(s => (
        <button
          key={`-${s}`}
          type="button"
          className="rounded border border-zinc-300 bg-white px-1 hover:bg-zinc-100"
          onClick={() => onStep(-s)}
        >
          −{s}
        </button>
      ))}
      <span className="w-12 text-center text-[#1e2228]">{value}</span>
      {steps
        .slice()
        .reverse()
        .map(s => (
          <button
            key={`+${s}`}
            type="button"
            className="rounded border border-zinc-300 bg-white px-1 hover:bg-zinc-100"
            onClick={() => onStep(s)}
          >
            +{s}
          </button>
        ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* The plate                                                           */
/* ------------------------------------------------------------------ */

type DragState =
  | { type: 'move'; key: PieceKey; startX: number; startY: number; cx: number; cy: number }
  | {
      type: 'corner';
      key: PieceKey;
      idx: number;
      startX: number;
      startY: number;
      dx: number;
      dy: number;
      a: number;
    }
  | {
      type: 'hole' | 'holeRX' | 'holeRY';
      key: PieceKey;
      startX: number;
      startY: number;
      hole: { cx: number; cy: number; r: number; ry: number };
      a: number;
    };

export function PackPlate({
  b,
  domain,
  avail,
  onFail,
  tune = false,
  plateIdOverride,
}: {
  b: PackBusiness;
  domain: string;
  /** Container width, measured by the caller. */
  avail: number;
  /** Plate image did not load — the caller swaps in the CSS scene. */
  onFail: () => void;
  /** Lab-only geometry editor. */
  tune?: boolean;
  /** Lab-only: force a specific plate instead of the trade's default. */
  plateIdOverride?: string;
}) {
  const plateId = plateIdOverride ?? plateForTrade(b.trade);
  const cfg = PLATES[plateId];

  const [overrides, setOverrides] = useState<Overrides>(() => loadOverrides());
  const [sel, setSel] = useState<PieceKey | null>(null);
  const [showJson, setShowJson] = useState(false);
  // "Clear ALL" is armed by a first click and only wipes on the second.
  const [armClear, setArmClear] = useState(false);
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'failed'>('idle');
  // What the loupe is watching — a corner index, or the die-cut — while dragged.
  const [loupe, setLoupe] = useState<{ key: PieceKey; target: number | 'hole' } | null>(null);
  const drag = useRef<DragState | null>(null);
  // The panel floats — null means the default corner, a point means dragged.
  const [panelPos, setPanelPos] = useState<{ x: number; y: number } | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const panelDrag = useRef<{ startX: number; startY: number; x: number; y: number } | null>(null);

  useEffect(() => {
    try {
      localStorage.setItem(TUNE_LS_KEY, JSON.stringify(overrides));
    } catch {
      /* private mode — tuning just won't survive a reload */
    }
  }, [overrides]);

  // Nothing in this browser? Fall back to the last Save on disk, so a cleared
  // or different browser picks the alignment back up. Lab only — the endpoint
  // is a Vite dev-server middleware and only the lab ever passes `tune`, so
  // this never fires in either host's production build.
  useEffect(() => {
    if (!tune) return;
    let live = true;
    if (Object.keys(loadOverrides()).length) return;
    loadOverridesFromDisk().then(disk => {
      if (live && disk) setOverrides(disk);
    });
    return () => {
      live = false;
    };
  }, []);

  const pieceKeys = keysOf(cfg);
  const rects = mergedRects(cfg, overrides[plateId]);
  const crop = cropOf(rects, pieceKeys);
  // Fill the container in BOTH directions: every plate crops to its own tight
  // bbox, so without upscaling two plates on one page render at two widths
  // and the narrower one reads as a mistake. Worst case is ~1.25x over the
  // 1024px source — soft on the photo, invisible under its grain, and the
  // live pieces are DOM so they stay crisp at any scale.
  const scale = avail > 0 ? avail / crop.w : 1;
  const site = `https://${domain}`;
  const shared = { guides: false, nameMode: 'auto' as const, logoMode: 'auto' as const };

  /**
   * `d` may be a fixed patch (checkboxes, "square corners") or a function of
   * the piece's CURRENT rect (steppers). The function form is read inside the
   * setOverrides updater against `prev`, not the render-time `rects` closure
   * — two stepper clicks fired before React re-renders (a fast double-click,
   * mashing +10 a few times) would otherwise both compute their delta from
   * the same stale value and only the last one would stick.
   */
  const patch = (key: PieceKey, d: Partial<PlateRect> | ((r: PlateRect) => Partial<PlateRect>)) =>
    setOverrides(prev => {
      const current = mergedRects(cfg, prev[plateId])[key];
      const delta = typeof d === 'function' ? d(current) : d;
      return {
        ...prev,
        [plateId]: { ...prev[plateId], [key]: { ...current, ...delta } },
      };
    });

  const capture = (e: React.PointerEvent) => {
    try {
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    } catch {
      /* synthetic events in tests have no real pointer */
    }
  };

  const startDrag = (key: PieceKey) => (e: React.PointerEvent) => {
    if (!tune) return;
    e.preventDefault();
    setSel(key);
    capture(e);
    drag.current = { type: 'move', key, startX: e.clientX, startY: e.clientY, cx: rects[key].cx, cy: rects[key].cy };
  };

  const startCorner = (key: PieceKey) => (idx: number) => (e: React.PointerEvent) => {
    if (!tune) return;
    e.preventDefault();
    e.stopPropagation();
    capture(e);
    setLoupe({ key, target: idx });
    const off = rects[key].quad?.[idx] ?? [0, 0];
    drag.current = {
      type: 'corner',
      key,
      idx,
      startX: e.clientX,
      startY: e.clientY,
      dx: off[0],
      dy: off[1],
      a: rects[key].a,
    };
  };

  const startHole = (key: PieceKey) => (mode: 'move' | 'rx' | 'ry') => (e: React.PointerEvent) => {
    if (!tune) return;
    e.preventDefault();
    e.stopPropagation();
    capture(e);
    setSel(key);
    setLoupe({ key, target: 'hole' });
    const nat = NAT[key];
    const { sx, sy } = pieceScale(rects[key], nat.w, nat.h);
    drag.current = {
      type: mode === 'move' ? 'hole' : mode === 'rx' ? 'holeRX' : 'holeRY',
      key,
      startX: e.clientX,
      startY: e.clientY,
      hole: holeOf(rects[key], sx, sy),
      a: rects[key].a,
    };
  };

  const onMove = (e: React.PointerEvent) => {
    const dr = drag.current;
    if (!tune || !dr) return;
    const sxp = (e.clientX - dr.startX) / scale;
    const syp = (e.clientY - dr.startY) / scale;
    if (dr.type === 'move') {
      patch(dr.key, { cx: Math.round(dr.cx + sxp), cy: Math.round(dr.cy + syp) });
    } else if (dr.type === 'hole' || dr.type === 'holeRX' || dr.type === 'holeRY') {
      // Screen delta into the piece's own rotated frame.
      const rad = (dr.a * Math.PI) / 180;
      const lx = sxp * Math.cos(rad) + syp * Math.sin(rad);
      const ly = -sxp * Math.sin(rad) + syp * Math.cos(rad);
      const h = dr.hole;
      const next =
        dr.type === 'hole'
          ? { ...h, cx: +(h.cx + lx).toFixed(1), cy: +(h.cy + ly).toFixed(1) }
          : dr.type === 'holeRX'
            ? { ...h, r: Math.max(2, +(h.r + lx).toFixed(1)) }
            : { ...h, ry: Math.max(2, +(h.ry + ly).toFixed(1)) };
      patch(dr.key, { hole: next });
    } else if (dr.type === 'corner') {
      // Screen delta into the piece's rotated frame.
      const rad = (dr.a * Math.PI) / 180;
      const lx = sxp * Math.cos(rad) + syp * Math.sin(rad);
      const ly = -sxp * Math.sin(rad) + syp * Math.cos(rad);
      const quad = (rects[dr.key].quad ?? [
        [0, 0],
        [0, 0],
        [0, 0],
        [0, 0],
      ]).map(p => [...p]) as [number, number][];
      quad[dr.idx] = [Math.round(dr.dx + lx), Math.round(dr.dy + ly)];
      patch(dr.key, { quad });
    }
  };
  const endDrag = () => {
    drag.current = null;
    setLoupe(null);
  };

  const renderPiece = (key: PieceKey, rect: PlateRect) => {
    // Reflow builds the piece on a box-shaped canvas so it re-lays-out around
    // it; the other fits render at natural size. Either way `Fit` applies the
    // scale that lands the canvas on the blank.
    const nat = NAT[key];
    const rc = fitOf(rect) === 'reflow' ? reflowCanvas(rect, nat.w, nat.h) : null;
    const size = rc ? { w: rc.w, h: rc.h } : {};
    // Every piece carries its own lit/shaded gradient — see Gloss.
    const lit = (node: React.ReactNode) => (
      <div className="relative">
        {node}
        <Gloss a={rect.a} />
      </div>
    );
    switch (key) {
      case 'sign':
        return lit(<YardSign b={b} {...shared} qrUrl={site} {...size} />);
      case 'magnet':
        return lit(<CarMagnet b={b} {...shared} {...size} />);
      case 'stack':
      case 'cardA':
        return lit(<CardFront b={b} {...shared} layout="center" {...size} />);
      case 'cardB':
        return lit(<CardBack b={b} {...shared} layout="center" qrUrl={`${site}/review`} {...size} />);
      default: {
        // A real transparent die-cut: the photo's own punched hole shows
        // through the overlay instead of a painted paper circle. The hole is
        // measured per blank (`rect.hole`, plate px) because the image model
        // never put it exactly where the hanger prints one; without an
        // override it falls back to the hanger's own printed geometry.
        const { sx, sy } = pieceScale(rect, nat.w, nat.h);
        const hole = holeOf(rect, sx, sy);
        const cx = (size.w ?? HANGER_W) / 2 + hole.cx / sx;
        const cy = hole.cy / sy;
        const rx = hole.r / sx;
        const ry = hole.ry / sy;
        // Ellipse form covers the circle too; with an explicit size the stops
        // are percentages of the ray, so the feather is ~1px of the short axis.
        const feather = Math.max(0, 100 - 100 / Math.max(1, Math.min(rx, ry)));
        const mask =
          `radial-gradient(ellipse ${rx}px ${ry}px at ${cx}px ${cy}px, ` +
          `transparent ${feather.toFixed(2)}%, black 100%)`;
        // Gloss lives INSIDE the mask, so the die-cut stays punched through it
        // instead of the highlight papering over the hole.
        return (
          <div className="relative" style={{ WebkitMaskImage: mask, maskImage: mask }}>
            <DoorHanger b={b} {...shared} qrUrl={site} {...size} holeCut={{ cy, ry }} />
            <Gloss a={rect.a} />
          </div>
        );
      }
    }
  };

  /** The full pieces block for packPlates.ts, tuned values baked in — only
      the pieces this plate actually has. */
  const mergedJson = () => {
    const strip = (r: PlateRect) => {
      const o: PlateRect = { cx: r.cx, cy: r.cy, w: r.w, h: r.h, a: r.a };
      if (r.fit && r.fit !== 'reflow') o.fit = r.fit;
      if (r.radius) o.radius = r.radius;
      if (r.quad && r.quad.some(p => p[0] !== 0 || p[1] !== 0)) o.quad = r.quad;
      if (r.hole) o.hole = r.hole;
      return o;
    };
    const hangerKeys = pieceKeys.filter(k => k.startsWith('hanger'));
    return JSON.stringify(
      {
        [plateId]: {
          pieces: {
            ...(rects.sign ? { sign: strip(rects.sign) } : {}),
            magnet: strip(rects.magnet),
            stack: strip(rects.stack),
            cardA: strip(rects.cardA),
            cardB: strip(rects.cardB),
            ...(hangerKeys.length ? { hangers: hangerKeys.map(k => strip(rects[k])) } : {}),
          },
        },
      },
      null,
      2
    );
  };

  const selRect = sel ? rects[sel] : null;
  // Cheap enough to re-read each render in a lab-only panel, and always fresh.
  const backup = tune ? loadBackup() : null;

  return (
    <div style={{ height: crop.h * scale, overflow: 'visible' }} className="relative">
      <div className="origin-top-left" style={{ transform: `scale(${scale})`, width: crop.w }}>
        <div
          className="relative overflow-hidden rounded-2xl ring-1 ring-zinc-200"
          style={{ width: crop.w, height: crop.h }}
          onPointerMove={onMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
        >
          <img
            src={packAsset(`/pack/plates/plate-${plateId}.jpg`)}
            alt=""
            style={{
              position: 'absolute',
              left: -crop.x,
              top: -crop.y,
              width: PLATE_IMG,
              height: PLATE_IMG,
              maxWidth: 'none',
            }}
            onError={onFail}
          />
          {pieceKeys.map(key => (
            <Fit
              key={key}
              rect={rects[key]}
              crop={crop}
              natW={NAT[key].w}
              natH={NAT[key].h}
              selected={tune && sel === key}
              tuneMode={tune}
              hud={1 / scale}
              dropShadow={key.startsWith('hanger') ? HANGER_SHADOW : undefined}
              hole={
                key.startsWith('hanger')
                  ? holeOf(
                      rects[key],
                      pieceScale(rects[key], NAT[key].w, NAT[key].h).sx,
                      pieceScale(rects[key], NAT[key].w, NAT[key].h).sy
                    )
                  : undefined
              }
              onHoleDown={tune && key.startsWith('hanger') ? startHole(key) : undefined}
              onPointerDown={tune ? startDrag(key) : undefined}
              onCornerDown={tune ? startCorner(key) : undefined}
            >
              {renderPiece(key, rects[key])}
            </Fit>
          ))}
          {/* Camera pass. Every layer here sits ABOVE the live pieces, which is
              the whole point: the same light, sheen and grain fall across photo
              and overlay alike, so the print stops reading as pasted-on. */}
          <div
            className="pointer-events-none absolute inset-0 z-10"
            style={{
              background: `radial-gradient(120% 90% at 70% 0%, ${b.brand.primary}0f, transparent 55%)`,
            }}
          />
          {/* Key light — the soft bloom the plates were all lit from. */}
          <div
            className="pointer-events-none absolute inset-0 z-10"
            style={{
              background:
                'radial-gradient(58% 48% at 24% 6%, rgba(255,253,247,0.34), transparent 70%)',
              mixBlendMode: 'screen',
            }}
          />
          {/* Specular sheen — a wide diagonal band, the gloss coming off stock
              held under one source. Screen-blended so it only ever lifts. */}
          <div
            className="pointer-events-none absolute inset-0 z-10"
            style={{
              background:
                'linear-gradient(104deg, transparent 24%, rgba(255,255,255,0.13) 41%,' +
                'rgba(255,255,255,0.21) 50%, rgba(255,255,255,0.11) 59%, transparent 76%)',
              mixBlendMode: 'screen',
            }}
          />
          <div
            className="pointer-events-none absolute inset-0 z-10"
            style={{
              background:
                'radial-gradient(120% 100% at 50% 40%, transparent 60%, rgba(30,34,40,0.12) 100%)',
            }}
          />
          {/* Falloff opposite the key — keeps the lit corner from flattening. */}
          <div
            className="pointer-events-none absolute inset-0 z-10"
            style={{
              background:
                'linear-gradient(122deg, transparent 46%, rgba(30,34,40,0.10) 100%)',
              mixBlendMode: 'multiply',
            }}
          />
          <div
            className="pointer-events-none absolute inset-0 z-10"
            style={{ backgroundImage: GRAIN, backgroundSize: '180px 180px', opacity: 0.5, mixBlendMode: 'soft-light' }}
          />
          {/* A second, finer pass at 1:1 — sensor noise rather than film grain.
              Overlay-blended, it speckles the clean vector print the same way
              the JPEG is already speckled, which is what sells the composite. */}
          <div
            className="pointer-events-none absolute inset-0 z-10"
            style={{
              backgroundImage: FINE_GRAIN,
              backgroundSize: '90px 90px',
              opacity: 0.34,
              mixBlendMode: 'overlay',
            }}
          />
        </div>
      </div>

      {/* Loupe — while a corner is dragged, the photograph under it, blown up,
          with the corner itself pinned at the crosshair. Lives in the outer
          box (unscaled, unclipped) and parks opposite the panel's home corner,
          so it never covers the corner being aimed. */}
      {tune && loupe
        ? (() => {
            const D = 148;
            const Z = 4.5;
            const lr = rects[loupe.key];
            let pt;
            if (loupe.target === 'hole') {
              const nat = NAT[loupe.key];
              const { sx, sy } = pieceScale(lr, nat.w, nat.h);
              const h = holeOf(lr, sx, sy);
              pt = localToPlate(lr, lr.w / 2 + h.cx, h.cy);
            } else {
              pt = cornerPlatePoint(lr, loupe.target);
            }
            return (
              <div
                className="pointer-events-none absolute z-30 overflow-hidden rounded-full bg-white shadow-lg ring-2 ring-fuchsia-400"
                style={{ left: 8, top: 8, width: D, height: D }}
              >
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    backgroundImage: `url(${packAsset(`/pack/plates/plate-${plateId}.jpg`)})`,
                    backgroundSize: `${PLATE_IMG * Z}px ${PLATE_IMG * Z}px`,
                    backgroundPosition: `${D / 2 - pt.x * Z}px ${D / 2 - pt.y * Z}px`,
                    imageRendering: 'auto',
                  }}
                />
                <svg width={D} height={D} style={{ position: 'absolute', inset: 0 }}>
                  <line x1={D / 2} y1={0} x2={D / 2} y2={D} stroke="rgba(217,70,239,0.55)" strokeWidth={1} />
                  <line x1={0} y1={D / 2} x2={D} y2={D / 2} stroke="rgba(217,70,239,0.55)" strokeWidth={1} />
                  <circle cx={D / 2} cy={D / 2} r={5} fill="none" stroke="#d946ef" strokeWidth={1.5} />
                </svg>
                <div className="absolute inset-x-0 bottom-1 text-center font-mono text-[9px] uppercase tracking-[0.14em] text-fuchsia-600">
                  {loupe.key} · {loupe.target === 'hole' ? 'die-cut' : ['tl', 'tr', 'br', 'bl'][loupe.target]}
                </div>
              </div>
            );
          })()
        : null}

      {tune ? (
        <div
          ref={panelRef}
          className="absolute z-20 w-[310px] rounded-lg bg-white/95 font-mono text-[11px] shadow-lg ring-1 ring-zinc-300"
          style={panelPos ? { left: panelPos.x, top: panelPos.y } : { right: 8, top: 8 }}
        >
          {/* Drag strip — the panel must never trap the piece under it. */}
          <div
            className="flex cursor-move select-none items-center justify-between rounded-t-lg border-b border-zinc-200 bg-zinc-50 px-3 py-1.5 text-[10px] uppercase tracking-[0.14em] text-zinc-500"
            style={{ touchAction: 'none' }}
            onPointerDown={e => {
              e.preventDefault();
              capture(e);
              const el = panelRef.current;
              panelDrag.current = {
                startX: e.clientX,
                startY: e.clientY,
                x: el ? el.offsetLeft : 0,
                y: el ? el.offsetTop : 0,
              };
            }}
            onPointerMove={e => {
              const pd = panelDrag.current;
              if (!pd) return;
              setPanelPos({ x: pd.x + e.clientX - pd.startX, y: pd.y + e.clientY - pd.startY });
            }}
            onPointerUp={() => {
              panelDrag.current = null;
            }}
            onPointerCancel={() => {
              panelDrag.current = null;
            }}
          >
            <span>tune · {plateId}</span>
            <span>⠿ drag</span>
          </div>
          <div className="p-3">
          <div className="mb-2 flex flex-wrap gap-1">
            {pieceKeys.map(k => (
              <button
                key={k}
                type="button"
                onClick={() => setSel(k)}
                className={`rounded px-1.5 py-0.5 ${sel === k ? 'bg-[#1e2228] text-white' : 'border border-zinc-300 bg-white hover:bg-zinc-100'}`}
              >
                {k}
              </button>
            ))}
          </div>
          {sel && selRect ? (
            <div className="flex flex-col gap-1.5">
              <Stepper label="x" value={String(selRect.cx)} steps={[10, 1]} onStep={d => patch(sel, r => ({ cx: r.cx + d }))} />
              <Stepper label="y" value={String(selRect.cy)} steps={[10, 1]} onStep={d => patch(sel, r => ({ cy: r.cy + d }))} />
              {/* Resizing moves the artwork on its own now that reflow is the
                  default; a piece pinned to contain won't visibly change on
                  its non-binding axis, which is the point of pinning it. */}
              <Stepper
                label="w"
                value={String(selRect.w)}
                steps={[10, 2]}
                onStep={d => patch(sel, r => ({ w: Math.max(20, r.w + d) }))}
              />
              <Stepper
                label="h"
                value={String(selRect.h)}
                steps={[10, 2]}
                onStep={d => patch(sel, r => ({ h: Math.max(20, r.h + d) }))}
              />
              <Stepper label="∠" value={selRect.a.toFixed(1)} steps={[2, 0.5]} onStep={d => patch(sel, r => ({ a: +(r.a + d).toFixed(1) }))} />
              <Stepper
                label="r"
                value={String(selRect.radius ?? 0)}
                steps={[8, 2]}
                onStep={d => patch(sel, r => ({ radius: Math.max(0, (r.radius ?? 0) + d) || undefined }))}
              />
              {/* Hangers only — line the punched hole up with the photo's. */}
              {sel.startsWith('hanger')
                ? (() => {
                    const { sx, sy } = pieceScale(selRect, NAT[sel].w, NAT[sel].h);
                    const h = holeOf(selRect, sx, sy);
                    // Delta read off `prev`, not off the render-time value —
                    // same reason the x/y/w/h steppers do.
                    const stepHole = (field: 'cx' | 'cy' | 'r' | 'ry', d: number) =>
                      patch(sel, prev => {
                        const p = pieceScale(prev, NAT[sel].w, NAT[sel].h);
                        const base = holeOf(prev, p.sx, p.sy);
                        const v = +(base[field] + d).toFixed(1);
                        const clamped = field === 'r' || field === 'ry' ? Math.max(2, v) : v;
                        return { hole: { ...base, [field]: clamped } };
                      });
                    return (
                      <>
                        <Stepper label="◎x" value={String(h.cx)} steps={[4, 1]} onStep={d => stepHole('cx', d)} />
                        <Stepper label="◎y" value={String(h.cy)} steps={[4, 1]} onStep={d => stepHole('cy', d)} />
                        <Stepper label="◎w" value={String(h.r)} steps={[4, 1]} onStep={d => stepHole('r', d)} />
                        <Stepper label="◎h" value={String(h.ry)} steps={[4, 1]} onStep={d => stepHole('ry', d)} />
                      </>
                    );
                  })()
                : null}
              {/* contain: keep aspect, centred (may leave a margin) ·
                  reflow: re-lay-out at the box size, like a div ·
                  stretch: fill exactly, distorting the artwork. */}
              <div className="mt-1 flex items-center gap-1">
                <span className="w-4 text-zinc-500">fit</span>
                {(['contain', 'reflow', 'stretch'] as const).map(f => (
                  <button
                    key={f}
                    type="button"
                    title={
                      f === 'contain'
                        ? 'Keep the aspect ratio, centred in the box'
                        : f === 'reflow'
                          ? 'Re-lay-out at the box size — type keeps its size, nothing distorts'
                          : 'Fill the box exactly — distorts the artwork'
                    }
                    onClick={() => patch(sel, { fit: f === 'reflow' ? undefined : f })}
                    className={`rounded px-1.5 py-0.5 ${
                      fitOf(selRect) === f
                        ? 'bg-[#1e2228] text-white'
                        : 'border border-zinc-300 bg-white hover:bg-zinc-100'
                    }`}
                  >
                    {f}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  className="rounded border border-zinc-300 bg-white px-1.5 py-0.5 hover:bg-zinc-100"
                  onClick={() => patch(sel, { quad: undefined })}
                >
                  square corners
                </button>
                <span className="text-zinc-400">drag ◉ to warp</span>
              </div>
            </div>
          ) : (
            <div className="text-zinc-500">Click a piece (or a chip) to select, drag to move.</div>
          )}
          <div className="mt-2 flex flex-wrap gap-1.5 border-t border-zinc-200 pt-2">
            {/* Writes the tuning to plate-tune.local.json in the working tree —
                it outlives the browser, and can be read straight off disk when
                the numbers are ready to be baked into packPlates.ts. */}
            <button
              type="button"
              title="Save this tuning to disk (plate-tune.local.json)"
              className={`rounded border px-1.5 py-0.5 ${
                saveState === 'saved'
                  ? 'border-emerald-500 bg-emerald-600 text-white'
                  : saveState === 'failed'
                    ? 'border-red-400 bg-white text-red-600'
                    : 'border-zinc-800 bg-[#1e2228] text-white hover:bg-black'
              }`}
              onClick={async () => {
                setSaveState('saving');
                const ok = await saveOverridesToDisk(overrides);
                setSaveState(ok ? 'saved' : 'failed');
                window.setTimeout(() => setSaveState('idle'), 2200);
              }}
            >
              {saveState === 'saving'
                ? 'Saving…'
                : saveState === 'saved'
                  ? 'Saved ✓'
                  : saveState === 'failed'
                    ? 'Save failed'
                    : 'Save'}
            </button>
            <button
              type="button"
              className="rounded border border-zinc-300 bg-white px-1.5 py-0.5 hover:bg-zinc-100"
              onClick={() => setShowJson(s => !s)}
            >
              {showJson ? 'Hide' : 'Show'} JSON
            </button>
            <button
              type="button"
              className="rounded border border-zinc-300 bg-white px-1.5 py-0.5 hover:bg-zinc-100"
              onClick={() =>
                setOverrides(prev => {
                  backupOverrides(prev);
                  const next = { ...prev };
                  delete next[plateId];
                  return next;
                })
              }
            >
              Reset plate
            </button>
            {/* Two-step: one click arms, the next wipes. A single mis-click
                next to "Reset plate" used to cost every plate's alignment. */}
            <button
              type="button"
              className={`rounded border px-1.5 py-0.5 ${
                armClear
                  ? 'border-red-500 bg-red-600 text-white'
                  : 'border-red-300 bg-white text-red-600 hover:bg-red-50'
              }`}
              onClick={() => {
                if (!armClear) {
                  setArmClear(true);
                  return;
                }
                setOverrides(prev => {
                  backupOverrides(prev);
                  return {};
                });
                setArmClear(false);
              }}
            >
              {armClear ? 'Really clear ALL?' : 'Clear ALL tuning'}
            </button>
            {backup ? (
              <button
                type="button"
                className="rounded border border-emerald-400 bg-white px-1.5 py-0.5 text-emerald-700 hover:bg-emerald-50"
                title="Put back the tuning that was last cleared"
                onClick={() => {
                  const b = loadBackup();
                  if (b) setOverrides(b);
                }}
              >
                Undo clear
              </button>
            ) : null}
          </div>
          {showJson ? (
            <textarea
              readOnly
              className="mt-2 h-40 w-full rounded border border-zinc-300 p-1 text-[10px] leading-tight"
              value={mergedJson()}
              onFocus={e => e.currentTarget.select()}
            />
          ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
